import { t } from '@glory/i18n';

/** Объект журнала, живых обновлений и чата: сделка (и лид — это та же запись). */
export const DEAL_ENTITY = 'deal';

/** Лид — рабочее место до сделки; сделка — то же, что выросло из лида. Запись одна. */
export const PHASES = ['lead', 'deal'] as const;
export type Phase = (typeof PHASES)[number];

/** Лид в очереди ждёт, пока его возьмут; взятый закреплён за кем-то. */
export const LEAD_STATES = ['queued', 'taken'] as const;
export type LeadState = (typeof LEAD_STATES)[number];

/** Этапы фиксированные: переименовать можно, удалить и переставить нельзя. */
export const STAGES = ['new', 'discovery', 'proposal', 'negotiation', 'contract'] as const;
export type Stage = (typeof STAGES)[number];

/** Итог: выиграна, проиграна (обе — у сделки) или отклонён (только лид). */
export const OUTCOMES = ['won', 'lost', 'rejected'] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** Что за работа: проект под ключ, почасово, подписка, дизайн и маркетинг. */
export const WORK_KINDS = ['fixed', 'tm', 'subscription', 'design_marketing'] as const;
export type WorkKind = (typeof WORK_KINDS)[number];

/** Откуда пришёл лид: те же значения, что у клиента. */
export const DEAL_SOURCES = ['site', 'email', 'referral', 'outbound', 'other'] as const;
export type DealSource = (typeof DEAL_SOURCES)[number];

/** Причины итога: проигрыш сделки и отклонение лида — два отдельных списка. */
export const REASON_KINDS = ['lost', 'rejected'] as const;
export type ReasonKind = (typeof REASON_KINDS)[number];

export const TITLE_MAX = 200;
export const NOTE_MAX = 2_000;
/** Примерный бюджет — копейки; выше миллиарда рублей — скорее опечатка. */
export const BUDGET_MAX = 1_000_000_000_00;

/** Календарная дата `YYYY-MM-DD`: отложенность считается по дням пояса студии, не по моментам. */
export type IsoDate = string;

/** Часть записи, от которой зависят правила переходов. */
export interface DealState {
  readonly phase: Phase;
  readonly leadState: LeadState | null;
  readonly stage: Stage | null;
  readonly outcome: Outcome | null;
  readonly stageBeforeClose: Stage | null;
  readonly postponedUntil: IsoDate | null;
  readonly responsibleId: string | null;
}

/** Открытая: итога нет (отложенная тоже открытая). */
export const isOpen = (deal: Pick<DealState, 'outcome'>): boolean => deal.outcome === null;

export const isLead = (deal: Pick<DealState, 'phase'>): boolean => deal.phase === 'lead';

export const isPostponed = (deal: Pick<DealState, 'outcome' | 'postponedUntil'>): boolean =>
  isOpen(deal) && deal.postponedUntil !== null;

/** Лид в очереди: автоматический источник завёл, никто не взял. */
export const isQueued = (
  deal: Pick<DealState, 'phase' | 'leadState' | 'outcome' | 'responsibleId'>,
): boolean =>
  isOpen(deal) && deal.phase === 'lead' && deal.leadState === 'queued' && deal.responsibleId === null;

/** «Взять» — только лид в очереди. */
export const canTake = isQueued;

/** Поля лида, которые меняет смена ответственного. */
export interface ReassignPatch {
  readonly leadState?: LeadState;
  readonly queuedAt?: Date;
  readonly takenAt?: Date | null;
}

/**
 * Что меняется у записи, когда ответственного назначили или сняли. У лида ответственный пуст ровно тогда,
 * когда он в очереди: снятый с ответственного лид возвращается в очередь (его можно «Взять»), назначенный
 * лиду из очереди — считается взятым. У сделки состояния лида нет: «без ответственного» — просто пустое поле.
 */
export function reassignPatch(
  deal: Pick<DealState, 'phase' | 'leadState'>,
  responsibleId: string | null,
  now: Date,
): ReassignPatch {
  if (deal.phase !== 'lead') return {};
  if (responsibleId === null) {
    return deal.leadState === 'queued' ? {} : { leadState: 'queued', queuedAt: now, takenAt: null };
  }
  return deal.leadState === 'queued' ? { leadState: 'taken', takenAt: now } : {};
}

/** «В сделку» — только из фазы «лид», пока он открыт. */
export const canConvert = (deal: Pick<DealState, 'phase' | 'outcome'>): boolean =>
  isOpen(deal) && deal.phase === 'lead';

// --- этапы ---------------------------------------------------------------------------------------

export const stageIndex = (stage: Stage): number => STAGES.indexOf(stage);

/** Следующий этап для «Дальше»; на последнем этапе — null (дальше только итог). */
export function nextStage(stage: Stage): Stage | null {
  return STAGES[stageIndex(stage) + 1] ?? null;
}

/** Предыдущий этап; на первом — null. */
export function previousStage(stage: Stage): Stage | null {
  return STAGES[stageIndex(stage) - 1] ?? null;
}

/**
 * Открытую сделку можно перевести на любой другой этап — вперёд и назад. Лид этапов не имеет, закрытая
 * сделка не двигается (сначала «Вернуть в работу»).
 */
export function canMoveStage(deal: Pick<DealState, 'phase' | 'outcome' | 'stage'>, to: Stage): boolean {
  return isOpen(deal) && deal.phase === 'deal' && deal.stage !== to;
}

// --- «В сделку» -----------------------------------------------------------------------------------

/** Чего может не хватать, чтобы лид стал сделкой. */
export const CONVERT_FIELDS = ['clientName', 'contact', 'workKind'] as const;
export type ConvertField = (typeof CONVERT_FIELDS)[number];

export interface ConvertInput {
  /** Название клиента. */
  readonly clientName: string | null | undefined;
  /** Есть ли у контакта хотя бы один способ связи. */
  readonly hasChannel: boolean;
  readonly workKind: WorkKind | null | undefined;
}

/**
 * Список недостающего для «В сделку»: название клиента, хотя бы один способ связи у контакта, вид
 * работы. Порядок — как на форме дозаполнения.
 */
export function convertIssues(input: ConvertInput): ConvertField[] {
  const missing: ConvertField[] = [];
  if ((input.clientName ?? '').trim() === '') missing.push('clientName');
  if (!input.hasChannel) missing.push('contact');
  if (input.workKind === null || input.workKind === undefined) missing.push('workKind');
  return missing;
}

// --- итоги ---------------------------------------------------------------------------------------

/**
 * «Выиграна» и «Проиграна» — у открытой сделки (с любого этапа), «Отклонён» — у открытого лида.
 */
export function canClose(deal: Pick<DealState, 'phase' | 'outcome'>, outcome: Outcome): boolean {
  if (!isOpen(deal)) return false;
  return outcome === 'rejected' ? deal.phase === 'lead' : deal.phase === 'deal';
}

/** Причина обязательна при проигрыше и отклонении; выигрыш обходится без неё. */
export const reasonRequired = (outcome: Outcome): boolean => outcome !== 'won';

/** Какой список причин нужен итогу: у выигрыша его нет. */
export function reasonKindFor(outcome: Outcome): ReasonKind | null {
  return outcome === 'lost' ? 'lost' : outcome === 'rejected' ? 'rejected' : null;
}

/** Поля записи, которые ставит итог. */
export interface ClosePatch {
  readonly outcome: Outcome;
  readonly stageBeforeClose: Stage | null;
  /** Закрытая запись не числится отложенной. */
  readonly postponedUntil: null;
}

export function closePatch(deal: Pick<DealState, 'stage'>, outcome: Outcome): ClosePatch {
  return {
    outcome,
    stageBeforeClose: outcome === 'rejected' ? null : deal.stage,
    postponedUntil: null,
  };
}

/** Кто и что может: от этого зависит «Вернуть в работу». */
export interface Who {
  readonly responsible: boolean;
  readonly readAll: boolean;
  readonly manage: boolean;
}

export type ReopenVerdict = 'ok' | 'not_closed' | 'won_forbidden' | 'forbidden';

/**
 * «Вернуть в работу»: проигранную и отклонённую — ответственный или тот, у кого `sales.read_all`;
 * выигранную — только `sales.manage`.
 */
export function reopenVerdict(deal: Pick<DealState, 'outcome'>, who: Who): ReopenVerdict {
  if (deal.outcome === null) return 'not_closed';
  if (deal.outcome === 'won') return who.manage ? 'ok' : 'won_forbidden';
  return who.responsible || who.readAll ? 'ok' : 'forbidden';
}

/** Куда возвращается запись: сделка — на этап, где была; отклонённый лид — снова лидом. */
export interface ReopenPatch {
  readonly phase: Phase;
  readonly stage: Stage | null;
}

export function reopenPatch(deal: Pick<DealState, 'phase' | 'stage' | 'stageBeforeClose'>): ReopenPatch {
  if (deal.phase === 'lead') return { phase: 'lead', stage: null };
  return { phase: 'deal', stage: deal.stageBeforeClose ?? deal.stage ?? 'new' };
}

// --- «Отложить» -----------------------------------------------------------------------------------

export type PostponeIssue = 'closed' | 'date_invalid' | 'past';

export const isIsoDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
};

/**
 * Отложить можно только открытую, до даты позже сегодняшней (по поясу студии). Даты ISO сравниваются
 * как строки — порядок букв совпадает с порядком дней.
 */
export function postponeIssue(
  deal: Pick<DealState, 'outcome'>,
  until: IsoDate,
  today: IsoDate,
): PostponeIssue | null {
  if (!isOpen(deal)) return 'closed';
  if (!isIsoDate(until)) return 'date_invalid';
  return until > today ? null : 'past';
}

/** Пора вернуть: день отложенности наступил (сегодня или раньше). */
export function postponeDue(deal: Pick<DealState, 'outcome' | 'postponedUntil'>, today: IsoDate): boolean {
  return isPostponed(deal) && (deal.postponedUntil ?? '') <= today;
}

// --- вторичность и текстовые поля -------------------------------------------------------------------

/**
 * Вторичное обращение: на момент создания у клиента есть другая не удалённая сделка или лид.
 */
export const isRepeatLead = (otherDeals: number): boolean => otherDeals > 0;

export const normalizeTitle = (value: string | null | undefined): string | null => {
  const clean = (value ?? '').replace(/\s+/g, ' ').trim();
  return clean === '' ? null : clean;
};

export const normalizeNote = (value: string | null | undefined): string | null => {
  const clean = (value ?? '').trim();
  return clean === '' ? null : clean;
};

/** Примерный бюджет: целые копейки, не меньше нуля; пусто — «не указан». */
export const isBudget = (value: number): boolean =>
  Number.isSafeInteger(value) && value >= 0 && value <= BUDGET_MAX;

// --- быстрый ввод лида ----------------------------------------------------------------------------

export type QuickLeadIssue = 'name_required' | 'name_too_long' | 'contact_required';

export interface QuickLeadInput {
  readonly name: string | null | undefined;
  readonly contact: string | null | undefined;
}

/** Минимум лида: имя и телефон или почта. Что именно введено — решает нормализатор клиентов. */
export function quickLeadIssues(input: QuickLeadInput): QuickLeadIssue[] {
  const issues: QuickLeadIssue[] = [];
  const name = normalizeTitle(input.name);
  if (name === null) issues.push('name_required');
  else if (name.length > TITLE_MAX) issues.push('name_too_long');
  if ((input.contact ?? '').trim() === '') issues.push('contact_required');
  return issues;
}

/** Строка контакта — почта, если в ней есть «@», иначе телефон. */
export const contactKindOf = (value: string): 'email' | 'phone' => (value.includes('@') ? 'email' : 'phone');

// --- названия для клиента (кабинет, позже) ------------------------------------------------------------

/** Ключи названий этапов для клиента: проигранное и отклонённое клиенту не показывается. */
export const CLIENT_STAGE_KEYS = [
  'lead',
  'new',
  'discovery',
  'proposal',
  'negotiation',
  'contract',
  'won',
] as const;
export type ClientStageKey = (typeof CLIENT_STAGE_KEYS)[number];

/**
 * Что клиент увидит вместо внутреннего этапа: лид — «Заявка получена», «Новая» — «Заявка принята»,
 * «Выясняем задачу» — «Обсуждаем задачу», «Оценка и КП» — «Готовим предложение», «Переговоры» —
 * «Предложение у вас», «Договор» — «Ждём подписания», «Выиграна» — «Договор подписан». Закрытое
 * без выигрыша — null: клиенту не показывается.
 */
export function clientStageKey(deal: Pick<DealState, 'phase' | 'stage' | 'outcome'>): ClientStageKey | null {
  if (deal.outcome === 'won') return 'won';
  if (deal.outcome !== null) return null;
  return deal.phase === 'lead' ? 'lead' : (deal.stage ?? 'new');
}

export function clientStageLabel(deal: Pick<DealState, 'phase' | 'stage' | 'outcome'>): string | null {
  const key = clientStageKey(deal);
  return key === null ? null : t(`sales.clientStages.${key}`);
}

// --- воронка ---------------------------------------------------------------------------------------

export interface BoardDeal {
  readonly stage: Stage | null;
  readonly budget: number | null;
}

export interface BoardColumn {
  readonly stage: Stage;
  readonly count: number;
  /** Сумма примерных бюджетов в копейках; сделки без бюджета в сумму не входят. */
  readonly budget: number;
}

/** Колонки доски: число сделок и сумма бюджетов по каждому этапу, этапы — в порядке воронки. */
export function boardColumns(deals: readonly BoardDeal[]): BoardColumn[] {
  return STAGES.map((stage) => {
    const own = deals.filter((deal) => deal.stage === stage);
    return {
      stage,
      count: own.length,
      budget: own.reduce((sum, deal) => sum + (deal.budget ?? 0), 0),
    };
  });
}

/** Сколько дней запись висит: целые сутки от `since` до `now`, не меньше нуля. */
export function ageInDays(since: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - since.getTime()) / 86_400_000));
}

// --- название чата -----------------------------------------------------------------------------------

/** Название для сравнения: без пробелов по краям, в одном регистре, «ё» как «е». */
const nameKey = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLowerCase().replaceAll('ё', 'е');

/**
 * Название чата сделки. Своё название записи — как есть; пустое или совпадающее с именем клиента (так
 * называется лид, пока ему не придумали название) ничего не говорит о работе, и чат сделки неотличим от
 * чата клиента, — тогда «<вид работы или «Обращение»> · <клиент>». Вычисляется, а не вводится: смена
 * названия, вида работы или имени клиента сама меняет название чата, без системных сообщений.
 */
export function dealChatTitle(input: {
  readonly title: string | null;
  readonly clientName: string;
  /** Вид работы словами; у записи без вида — «Обращение». */
  readonly kindLabel: string;
}): string {
  const title = (input.title ?? '').trim();
  const client = input.clientName.trim();
  if (title !== '' && nameKey(title) !== nameKey(client)) return title;
  return client === '' ? input.kindLabel : `${input.kindLabel} · ${client}`;
}
